/**
 * Bridges error-variant toasts into the error_events register.
 *
 * All destructive/error toasts (shadcn useToast with variant='destructive' and
 * sonner's toast.error) flow through logHandledUiError, which fingerprints and
 * writes via the existing log_error_event RPC (src/lib/errorLogger).
 *
 * No visual changes; this is a pure side-channel.
 */
import { toast as sonnerToast } from "sonner";
import { logError } from "@/lib/errorLogger";

const RECENT = new Map<string, number>(); // title|desc -> ts, 10s local dedupe
const DEDUPE_MS = 10_000;

function stringify(node: unknown): string {
  if (node == null) return "";
  if (typeof node === "string") return node;
  if (typeof node === "number" || typeof node === "boolean") return String(node);
  try {
    // React nodes / objects: best-effort short form
    return JSON.stringify(node).slice(0, 300);
  } catch {
    return "";
  }
}

export function logHandledUiError(
  title: unknown,
  description?: unknown,
  err?: unknown,
): void {
  try {
    // Skip when offline — bad wifi shouldn't flood the register.
    if (typeof navigator !== "undefined" && navigator.onLine === false) return;

    const t = stringify(title).trim();
    const d = stringify(description).trim();
    const message = (t && d ? `${t}: ${d}` : t || d).slice(0, 500);
    if (!message) return;

    const now = Date.now();
    const key = message;
    const last = RECENT.get(key) || 0;
    if (now - last < DEDUPE_MS) return;
    RECENT.set(key, now);

    const e = err as { message?: string; stack?: string } | undefined;
    void logError({
      errorClass: "handled_ui_error",
      message,
      stack: e?.stack,
      context: {
        severity: "low",
        toast_title: t || null,
        toast_description: d || null,
        error_message: e?.message || null,
      },
    }).catch(() => {
      // Never toast about a logging failure.
    });
  } catch (loggingErr) {
    // eslint-disable-next-line no-console
    console.warn("[errorToast] failed to log handled ui error", loggingErr);
  }
}

let INSTALLED = false;

/**
 * Monkey-patch sonner's `toast.error` so every call also logs.
 * shadcn useToast is bridged inside src/hooks/use-toast.ts (variant check).
 */
export function installToastErrorLogging(): void {
  if (INSTALLED) return;
  INSTALLED = true;

  try {
    const original = sonnerToast.error.bind(sonnerToast);
    (sonnerToast as unknown as { error: typeof sonnerToast.error }).error =
      ((message: Parameters<typeof sonnerToast.error>[0],
        data?: Parameters<typeof sonnerToast.error>[1]) => {
        logHandledUiError(message, (data as { description?: unknown } | undefined)?.description);
        return original(message, data);
      }) as typeof sonnerToast.error;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[errorToast] could not patch sonner.error", e);
  }
}
